import React, { useEffect, useState } from 'react'
import {
  Box,
  FormControlLabel,
  Grid,
  InputLabel,
  Radio,
  RadioGroup,
} from '@material-ui/core'
import { FormContext, useForm } from 'react-hook-form'

import { Container, Page, RoundedBorder } from 'components'
import {
  DepartmentForm,
  ExportFormatDialog,
  Header,
  ReportVersionTable,
  TrainingDepartmentPdf,
  TrainingPdf,
} from './components'

import useSnackbar from 'hooks/useSnackbar'
import useFetch from 'hooks/useFetch'
import useStyles from './styles'

import * as service from 'service'
import schema from './components/DepartmentForm/schema'
import constants from 'constants/index'
import helpers from 'helpers'
import { isEmpty } from 'lodash'

const TrainingReport = () => {
  const [currentReportVersion, setCurrentReportVersion] = useState()
  const [exportingPdf, setExportingPdf] = useState(false)
  const [fetchParams, setFetchParams] = useState(null)
  const [generateDepartment, setGenerateDepartment] = useState(false)
  const [isComplete, setIsComplete] = useState(true)
  const [refresher, setRefresher] = useState(false)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isLoadingExcel, setLoadingExcel] = useState(false)
  const [isExportingPdf, setIsExportingPdf] = useState(false)
  const classes = useStyles()
  const snackbar = useSnackbar()

  const formMethods = useForm({
    validationSchema: schema(!isComplete),
    defaultValues: {
      departments: [],
      exportFormat: '',
      dataAction: constants.reportVersions.DESTROY_VERSION_ID_ACTION,
    },
  })

  const { watch, triggerValidation, handleSubmit } = formMethods

  const { response: reportResponse, isLoading: isLoadingReportData } = useFetch(
    isComplete
      ? service.dponet.trainningJourneys.showUserCertifications
      : service.dponet.trainningJourneys.listDepartmentCertifications,
    fetchParams,
    [],
    !!fetchParams,
  )
  const users = reportResponse?.data?.users
  const usersStats = reportResponse?.data?.stats
  const departmentData = reportResponse?.data?.departments
  const departmentStats = reportResponse?.data?.stats

  const { response: responseQrCode, isLoading: isLoadingQrCode } = useFetch(
    service.dponet.reports.generateQrcode,
    { report: constants.reports.KIND.TRAINING },
  )

  const qrCode = responseQrCode?.data

  const isLoading = isLoadingReportData || isLoadingQrCode || isExportingPdf

  const { EXPORT_FORMAT_TYPES } = constants.reports

  const handleChange = (event) => {
    setIsComplete(event.target.value === 'complete')
  }

  const handleCloseExportFormatDialog = () => setIsDialogOpen(false)
  const handleRefresh = () => setRefresher((prev) => !prev)

  const mixPanel = (action) => {
    helpers.mixpanel.track('Relatório completo', 'Relatório por Departamento', {
      action,
    })
  }

  const handleOpenExportFormatDialog = async () => {
    const valid = await triggerValidation()
    if (!valid) return
    setIsDialogOpen(true)
  }

  const handleSelectExcelExport = async (params) => {
    setLoadingExcel(true)
    try {
      const excelParams = {
        departmentIds: params?.departmentIds || [],
        shouldCreateVersion:
          params?.dataAction !==
          constants.reportVersions.DOWNLOAD_ONLY_ID_ACTION,
      }

      const response = await service.dponet.trainningJourneys.getStatsExcel(
        excelParams,
      )
      const { url } = response?.data || {}
      window.open(url)

      snackbar.open({
        message: 'Relatório Excel exportado com sucesso!',
        variant: 'success',
      })
    } catch (error) {
      snackbar.open({
        message: helpers.formatters.errorMessage(
          error?.response?.data?.error || 'Falha ao exportar relatório Excel.',
        ),
        variant: 'error',
      })
    } finally {
      setLoadingExcel(false)
    }
  }

  const onConfirmExport = async (data) => {
    if (isLoading) return

    handleCloseExportFormatDialog()

    let currentParams = {}

    if (isComplete) {
      mixPanel('gerar-relatorio-completo')
      setFetchParams({})
    } else {
      const selectedDepartments = watch('departments')
      if (!selectedDepartments || isEmpty(selectedDepartments)) {
        return snackbar.open({
          variant: 'error',
          message: 'Por favor, selecione ao menos um departamento.',
        })
      }

      mixPanel('gerar-relatorio-departamento')
      const departmentIds = selectedDepartments.map(
        (department) => department.id,
      )
      currentParams = { departmentIds }
    }

    setFetchParams(currentParams)

    if (data.exportFormat === EXPORT_FORMAT_TYPES.PDF) {
      setIsExportingPdf(true)
    } else {
      await handleSelectExcelExport({
        dataAction: data?.dataAction,
        ...currentParams,
      })
      handleRefresh()
    }
  }

  useEffect(() => {
    if (!isLoadingReportData && reportResponse && isExportingPdf) {
      if (isComplete && users?.length > 0) {
        setExportingPdf(true)
      } else if (!isComplete && departmentData?.length > 0) {
        setGenerateDepartment(true)
      } else {
        snackbar.open({ message: 'Nenhum dado encontrado para o relatório.' })
      }
      setFetchParams(null)
      setIsExportingPdf(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportResponse, isLoadingReportData, isExportingPdf])

  return (
    <Page className={classes.root} title="Relatório de Treinamentos">
      <Box position="relative">
        <RoundedBorder />
      </Box>
      <Container maxWidth={false} spacingXl={15} spacingLg={10} spacingMd={5}>
        <Box py={5}>
          <FormContext {...formMethods}>
            <Grid container spacing={3} justifyContent="space-between">
              <Grid item xs={12}>
                <Header
                  type="training"
                  isLoading={isLoading}
                  buttonAction={handleOpenExportFormatDialog}
                  disabled={exportingPdf}
                  reportTour="treinamentos"
                />
              </Grid>

              <Grid item xs={12}>
                <InputLabel className={classes.label}>
                  Escolha a versão do relatório:
                </InputLabel>
                <RadioGroup
                  value={isComplete ? 'complete' : 'department'}
                  onChange={handleChange}
                >
                  <FormControlLabel
                    value="complete"
                    control={<Radio classes={{ checked: classes.checked }} />}
                    className={classes.radioLabel}
                    label="Relatório Completo"
                  />
                  <FormControlLabel
                    value="department"
                    control={<Radio classes={{ checked: classes.checked }} />}
                    className={classes.radioLabel}
                    label="Relatório por Departamento"
                  />
                </RadioGroup>
              </Grid>
              {!isComplete && !isLoading && (
                <Grid item xs={12}>
                  <DepartmentForm />
                </Grid>
              )}

              <Grid item xs={12}>
                <ReportVersionTable
                  currentReportVersion={currentReportVersion}
                  kind={constants.reportVersions.KIND.TRAINING}
                  refresher={refresher}
                  setCurrentReportVersion={setCurrentReportVersion}
                />
              </Grid>
            </Grid>
            {users && (
              <TrainingPdf
                exportingPdf={exportingPdf}
                setExportingPdf={setExportingPdf}
                refresh={handleRefresh}
                shouldCreateVersion={!currentReportVersion?.id}
                users={users}
                stats={usersStats}
                qrCode={qrCode}
              />
            )}

            {departmentData && (
              <TrainingDepartmentPdf
                generateDepartment={generateDepartment}
                setGenerateDepartment={setGenerateDepartment}
                refresh={handleRefresh}
                shouldCreateVersion={!currentReportVersion?.id}
                departmentData={departmentData}
                stats={departmentStats}
                qrCode={qrCode}
              />
            )}
            <ExportFormatDialog
              open={isDialogOpen}
              onClose={handleCloseExportFormatDialog}
              onConfirmExport={handleSubmit(onConfirmExport)}
              isLoadingExcel={isLoadingExcel}
              currentReportVersion={currentReportVersion}
            />
          </FormContext>
        </Box>
      </Container>
    </Page>
  )
}

export default TrainingReport
